import React, { useState, useEffect } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { useNavigate } from 'react-router-dom';
import './CardForm.css';
import { FaLinkedin, FaFacebook, FaInstagram, FaYoutube, FaTwitter, FaGithub, FaPhoneAlt, FaEnvelope, FaVideo, FaMapMarkerAlt, FaGlobe } from 'react-icons/fa';
import { BsMicrosoftTeams } from "react-icons/bs";

const CardForm = () => {
    const [activeTab, setActiveTab] = useState('Information');
    const [errors, setErrors] = useState({});
    const API_URL = process.env.REACT_APP_API_URL;

    const [cardData, setCardData] = useState({
        name: '',
        position: '',
        phone: '',
        email: '',
        address: '',
        website: '',
        companyName: '',
        backgroundColor: '#ffffff',
        backgroundImage: '',
        qrCode: '',
        linkedin: '',
        facebook: '',
        instagram: '',
        youtube: '',
        twitter: '',
        teams: '',
        zoom: '',
        github: ''
    });

    const navigate = useNavigate();

    useEffect(() => {
        const generateQRCode = async () => {
            try {
                const qrData = JSON.stringify({
                    name: cardData.name,
                    position: cardData.position,
                    phone: cardData.phone,
                    email: cardData.email,
                    address: cardData.address,
                    website: cardData.website,
                });
                const qrCode = await QRCode.toDataURL(qrData);
                setCardData(prevState => ({ ...prevState, qrCode }));
            } catch (error) {
                console.error('Error generating QR code:', error);
            }
        };

        generateQRCode();
    }, [cardData.name, cardData.position, cardData.phone, cardData.email, cardData.address, cardData.website]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCardData({ ...cardData, [name]: value });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        const formData = new FormData();
        formData.append('image', file);

        axios.post(`${API_URL}/api/upload-image`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
        .then(response => {
            setCardData({ ...cardData, backgroundImage: response.data.imageUrl });
        })
        .catch(error => {
            console.error('Error uploading image:', error);
        });
    };

    const validateURLs = () => {
        const urlFields = ['linkedin', 'facebook', 'instagram', 'youtube', 'twitter', 'teams', 'zoom', 'github'];
        let valid = true;
        const newErrors = {};

        urlFields.forEach(field => {
            const url = cardData[field];
            if (url && !/^(https?:\/\/)?([\w-]+(\.[\w-]+)+\/?)([^\s]*)?$/.test(url)) {
                newErrors[field] = 'Please enter a valid URL.';
                valid = false;
            }
        });

        setErrors(newErrors);
        return valid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
    
        if (validateURLs()) {
            try {
                const token = localStorage.getItem('token');  // Retrieve the token from localStorage
    
                await axios.post(
                    `${API_URL}/api/cards`,
                    cardData,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`  // Include the token in the Authorization header
                        }
                    }
                );
    
                alert('Card information saved!');
                navigate('/cards');  // Redirect to the cards page after successful submission
            } catch (error) {
                console.error('Error saving card information:', error);
                alert('An error occurred while saving the card information.');
            }
        } else {
            alert('Please fix the errors before submitting.');
        }
    };    

    const renderSocialIcons = () => {
        const icons = [
            { name: 'linkedin', url: cardData.linkedin, icon: <FaLinkedin />, show: !!cardData.linkedin },
            { name: 'facebook', url: cardData.facebook, icon: <FaFacebook />, show: !!cardData.facebook },
            { name: 'instagram', url: cardData.instagram, icon: <FaInstagram />, show: !!cardData.instagram },
            { name: 'youtube', url: cardData.youtube, icon: <FaYoutube />, show: !!cardData.youtube },
            { name: 'twitter', url: cardData.twitter, icon: <FaTwitter />, show: !!cardData.twitter },
            { name: 'teams', url: cardData.teams, icon: <BsMicrosoftTeams />, show: !!cardData.teams },
            { name: 'zoom', url: cardData.zoom, icon: <FaVideo />, show: !!cardData.zoom },
            { name: 'github', url: cardData.github, icon: <FaGithub />, show: !!cardData.github },

        ];
    
        return icons
            .filter(iconObj => iconObj.show)  // Only include icons where the 'show' field is true
            .map(({ name, url, icon }) => (
                <a key={name} href={url} target="_blank" rel="noopener noreferrer" className="icon">
                    {icon}
                </a>
            ));
    };     

    return (
        <div className="card-form-container-unique">
            <div className="form-and-preview-unique">
                <div className="preview-and-qr-unique">
                    <div 
                        className="card-preview-unique" 
                        style={{
                            backgroundColor: cardData.backgroundColor,
                            backgroundImage: cardData.backgroundImage ? `url(${cardData.backgroundImage})` : 'none'
                        }}
                    >
                        {/* Top Banner / Background Profile Area */}
                        <div className="card-preview-top-unique" 
                            style={{
                                backgroundColor: cardData.backgroundImage ? 'transparent' : cardData.backgroundColor,
                                backgroundImage: cardData.backgroundImage ? `url(${cardData.backgroundImage})` : 'none'
                            }}
                        >
                        </div>

                        {/* Bottom Content Area */}
                        <div className="card-preview-bottom-unique">
                            <div className="profile-header-unique">
                                <h3>{cardData.name || "Your Name"}</h3>
                                <p className="position-text-unique">{cardData.position || "Position"}</p>
                                {cardData.companyName && <p className="company-text-unique">{cardData.companyName}</p>}
                            </div>

                            {/* Partition Divider */}
                            <hr className="card-divider-unique" />

                            {/* Direct Contact List - Row by Row with Icons */}
                            <div className="contact-rows-unique">
                                {cardData.phone && (
                                    <div className="contact-row-item-unique">
                                        <span className="contact-icon-wrapper-unique"><FaPhoneAlt /></span>
                                        <a href={`tel:${cardData.phone}`}>{cardData.phone}</a>
                                    </div>
                                )}
                                {cardData.email && (
                                    <div className="contact-row-item-unique">
                                        <span className="contact-icon-wrapper-unique"><FaEnvelope /></span>
                                        <a href={`mailto:${cardData.email}`}>{cardData.email}</a>
                                    </div>
                                )}
                                {cardData.address && (
                                    <div className="contact-row-item-unique">
                                        <span className="contact-icon-wrapper-unique"><FaMapMarkerAlt /></span>
                                        <a href={`http://maps.google.com/?q=${encodeURIComponent(cardData.address)}`} target="_blank" rel="noopener noreferrer">
                                            {cardData.address}
                                        </a>
                                    </div>
                                )}
                                {cardData.website && (
                                    <div className="contact-row-item-unique">
                                        <span className="contact-icon-wrapper-unique"><FaGlobe /></span>
                                        <a 
                                            href={/^(https?:\/\/)/i.test(cardData.website) ? cardData.website : `https://${cardData.website}`} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                        >
                                            {cardData.website}
                                        </a>
                                    </div>
                                )}
                            </div>

                            {/* Partition Divider for Social Media Block */}
                            {renderSocialIcons().length > 0 && <hr className="card-divider-unique" />}

                            {/* Social Grid Container */}
                            <div className="social-icons-unique">
                                {renderSocialIcons()}
                            </div>
                        </div>
                    </div>
                    
                    {cardData.qrCode && (
                        <div className="qr-code-container-unique">
                            <h4>QR Code:</h4>
                            <img src={cardData.qrCode} alt="QR Code" className="qr-code-image-unique" />
                        </div>
                    )}
                </div>
                <div className="card-edit-form-unique">
                    <div className="tabs-unique">
                        <button 
                            type="button" 
                            className={activeTab === 'Information' ? 'active' : ''} 
                            onClick={() => setActiveTab('Information')}
                        >
                            Information
                        </button>
                        <button 
                            type="button" 
                            className={activeTab === 'Social Links' ? 'active' : ''} 
                            onClick={() => setActiveTab('Social Links')}
                        >
                            Social Links
                        </button>
                        <button 
                            type="button" 
                            className={activeTab === 'Customization' ? 'active' : ''} 
                            onClick={() => setActiveTab('Customization')}
                        >
                            Customization
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="form-tab-content">
                        {activeTab === 'Information' && (
                            <div className="tab-content-unique">
                                <label>Name:</label>
                                <input type="text" name="name" value={cardData.name} onChange={handleChange} />
                                
                                <label>Position:</label>
                                <input type="text" name="position" value={cardData.position} onChange={handleChange} />
                                
                                <label>Company Name:</label>
                                <input type="text" name="companyName" value={cardData.companyName} onChange={handleChange} />
                                
                                <label>Phone:</label>
                                <input type="text" name="phone" value={cardData.phone} onChange={handleChange} />
                                
                                <label>Email:</label>
                                <input type="email" name="email" value={cardData.email} onChange={handleChange} />
                                
                                <label>Address:</label>
                                <input type="text" name="address" value={cardData.address} onChange={handleChange} />
                                
                                <label>Website:</label>
                                <input type="text" name="website" value={cardData.website} onChange={handleChange} />
                            </div>
                        )}

                        {activeTab === 'Social Links' && (
                            <div className="tab-content-unique">
                                <label>LinkedIn:</label>
                                <input type="url" name="linkedin" value={cardData.linkedin} onChange={handleChange} />
                                {errors.linkedin && <span className="error-message">{errors.linkedin}</span>}

                                <label>Facebook:</label>
                                <input type="url" name="facebook" value={cardData.facebook} onChange={handleChange} />
                                {errors.facebook && <span className="error-message">{errors.facebook}</span>}

                                <label>Instagram:</label>
                                <input type="url" name="instagram" value={cardData.instagram} onChange={handleChange} />
                                {errors.instagram && <span className="error-message">{errors.instagram}</span>}

                                <label>YouTube:</label>
                                <input type="url" name="youtube" value={cardData.youtube} onChange={handleChange} />
                                {errors.youtube && <span className="error-message">{errors.youtube}</span>}

                                <label>Twitter:</label>
                                <input type="url" name="twitter" value={cardData.twitter} onChange={handleChange} />
                                {errors.twitter && <span className="error-message">{errors.twitter}</span>}

                                <label>GitHub:</label>
                                <input type="url" name="github" value={cardData.github} onChange={handleChange} />
                                {errors.github && <span className="error-message">{errors.github}</span>}

                                <label>Zoom:</label>
                                <input type="url" name="zoom" value={cardData.zoom} onChange={handleChange} />
                                {errors.zoom && <span className="error-message">{errors.zoom}</span>}

                                <label>Teams:</label>
                                <input type="url" name="teams" value={cardData.teams} onChange={handleChange} />
                                {errors.teams && <span className="error-message">{errors.teams}</span>}
                            </div>
                        )}

                        {activeTab === 'Customization' && (
                            <div className="tab-content-unique">
                                <label htmlFor="backgroundColor">Background Color:</label>
                                <input
                                    type="color"
                                    id="backgroundColor"
                                    name="backgroundColor"
                                    value={cardData.backgroundColor}
                                    onChange={handleChange}
                                />
                                <label htmlFor="backgroundImage">Background Image:</label>
                                <input
                                    type="file"
                                    id="backgroundImage"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                            </div>
                        )}

                        <button type="submit" className="save-btn-unique">Save Card</button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CardForm;
